import React, { useState } from 'react';
import { t } from 'i18next';
import { useClient } from 'jsonapi-react';
import moment from 'moment';
import { useEffect } from 'react';
import { TextareaAutosize } from '@/components/ui/textarea-autosize';
import { useCheckIncludeProfiles } from '@/hooks/useAuthorityLevel';
import toast from 'react-hot-toast';
import { useSession } from '@/hooks/useSession';
import { PageContent, PageContentHeader, PageContentTitle } from '@/components/custom/page';
import { FormCheck, FormField, FormSwitch, FormSwitchWrapper, FormTaxonomy, FormText } from '@/components/custom/form';
import { Label } from '@/components/ui/label';
import { FieldGroup } from '@/components/ui/field';

export function CourseStepAbout(props) {
  const { step, form, creatorId, isEditing } = props;
  const client = useClient();
  const [taxonomies, setTaxonomies] = useState();
  const [isLoadingTaxonomies, setIsLoadingTaxonomies] = useState(false);
  const [isOpenModalTaxonomies, setIsOpenModalTaxonomies] = useState(false);
  const isAdmin = useCheckIncludeProfiles({ only: ['admin'] });

  const getTaxonomies = async () => {
    setIsLoadingTaxonomies(true);
    try {
      const { data } = await client.fetch('taxonomies');
      setTaxonomies(data);
    } catch {
      toast.error('Erro ao carregar taxonomias');
    } finally {
      setIsLoadingTaxonomies(false);
    }
  };

  useEffect(() => {
    getTaxonomies();
  }, []);

  const currentUser = useSession()?.session?.user;
  const allowToChangeAuthorial = currentUser.id === creatorId || currentUser?.profileNames.includes('Administrador');

  return (
    step === 'sobre' && (
      <PageContent
        bg
        className="mb-12 animate-fade-in gap-0"
      >
        <PageContentHeader>
          <PageContentTitle>{t('courses.buttonNewCourse')}</PageContentTitle>
        </PageContentHeader>

        <FieldGroup>
          <FormField
            label={`*${t('filter.courseName')}`}
            placeholder={t('courses.placeholderCourseName')}
            name="title"
            type="text"
            value={form?.values?.title}
            onChange={e => form?.setFieldValue('title', e.target.value)}
            hasError={form?.touched?.title && form?.errors?.title}
            errorText={form?.errors?.title}
          />

          <FormField
            label={`*${t('courses.aboutCourse')}`}
            id="about"
            hasError={form?.touched?.about && form?.errors?.about}
            errorText={form?.errors?.about}
          >
            <TextareaAutosize
              placeholder={t('courses.aboutCoursePlaceholder')}
              id="about"
              name="about"
              value={form?.values?.about}
              aria-invalid={Boolean(form?.touched?.about && form?.errors?.about)}
              onChange={e => form.setFieldValue('about', e.target.value)}
            />
          </FormField>

          <FormCheck
            label="O conteúdo do curso é público"
            checked={form.values['is-public']}
            onCheckedChange={checked => form.setFieldValue('is-public', !!checked)}
          />

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <FormField
              label="*Data de conclusão do curso"
              placeholder={t('lms.typeAnumber')}
              name="finished-at"
              type="date"
              value={form?.values?.['finished-at'] ? moment(form?.values?.['finished-at']).format('YYYY-MM-DD') : ''}
              onChange={e => form.setFieldValue('finished-at', e.target.value)}
              hasError={form?.touched?.['finished-at'] && form?.errors?.['finished-at']}
              errorText={form?.errors?.['finished-at']}
            />
          </div>

          <FormTaxonomy
            label={`*${t('tasks.mattersRelated')}`}
            isLoading={isLoadingTaxonomies}
            taxonomies={taxonomies}
            selectedTaxonomies={form?.values?.selectedTaxonomies}
            setFieldValue={form?.setFieldValue}
            errors={form?.errors}
            touched={form?.touched}
            isOpenModalTaxonomies={isOpenModalTaxonomies}
            setIsOpenModalTaxonomies={setIsOpenModalTaxonomies}
          />

          <fieldset className="space-y-1">
            <Label asChild>
              <legend>Inteligência Artificial</legend>
            </Label>

            <FormSwitchWrapper>
              <FormSwitch
                label="Quizz durante a aula"
                checked={form?.values?.['ai-options']?.quizzes}
                onCheckedChange={checked => {
                  form?.setFieldValue('ai-options.quizzes', checked);
                }}
              />
              <FormSwitch
                label="Mapa mental"
                checked={form?.values?.['ai-options']?.mindmaps}
                onCheckedChange={checked => {
                  form?.setFieldValue('ai-options.mindmaps', checked);
                }}
              />
              <FormSwitch
                label="Resumo automático"
                checked={form?.values?.['ai-options']?.['study-guide']}
                onCheckedChange={checked => {
                  form?.setFieldValue('ai-options.study-guide', checked);
                }}
              />
            </FormSwitchWrapper>
          </fieldset>

          {(allowToChangeAuthorial || !isEditing) && (
            <fieldset className="space-y-1">
              <Label asChild>
                <legend>Autoria do curso</legend>
              </Label>

              <FormSwitchWrapper>
                <FormSwitch
                  label={
                    <span>
                      Este curso é de minha autoria e <strong>somente eu posso editá-lo.</strong>
                    </span>
                  }
                  checked={form?.values?.['authored-by-user']}
                  onCheckedChange={checked => {
                    form?.setFieldValue('authored-by-user', checked);
                  }}
                />

                {isAdmin && (
                  <FormSwitch
                    label={
                      <span>
                        Este curso é <strong>oficial;</strong> apenas a equipe administrativa têm permissão para editá-lo.
                      </span>
                    }
                    checked={form?.values?.['official-content']}
                    onCheckedChange={checked => {
                      form?.setFieldValue('official-content', checked);
                    }}
                  />
                )}
              </FormSwitchWrapper>
            </fieldset>
          )}

          <FormText>
            <strong>* Campo obrigatório!</strong>
          </FormText>
        </FieldGroup>
      </PageContent>
    )
  );
}
